import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../../utils/toast";
import { AlertTriangle } from "lucide-react";
import { Modal } from "../../../../components/ui/Modal";
import { Button } from "../../../../components/ui/Button";
import { deleteIntegration } from "../../../../api/integrations";
import { deleteIntegrationApi } from "../../../../api/user";

// Confirmación destructiva de eliminación de una integración. Prefiere el
// endpoint por id; cae al legacy PUT integration (por alias) cuando no hay id.
export default function DeleteIntegrationModal({
  open,
  integrationId,
  alias,
  integrationName,
  clientId,
  onClose,
  onSuccess,
}) {
  const { t } = useTranslation();
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      const base = integrationId
        ? deleteIntegration(integrationId)
        : deleteIntegrationApi({ clientId, alias, integrationName });

      // Ambas APIs capturan errores y resuelven {error}: lo convertimos en
      // rechazo para que toast.promise no anuncie éxito sobre una falla.
      const request = base.then((res) => {
        if (res && "error" in res) {
          const err = new Error("deleteIntegration failed");
          err.status = res.error?.statusCode;
          err.data = res.error;
          throw err;
        }
        return res;
      });

      // Sin entrada `error`: el catch de abajo es el dueño del toast de error
      // (con ambas, cada fallo apilaba dos toasts por el mismo error).
      await toast.promise(request, {
        pending: t("Eliminando..."),
        success: t("¡Integración eliminada correctamente!"),
      });

      if (typeof onSuccess === "function") onSuccess();
      onClose();
    } catch (error) {
      console.error(error);

      if (error?.status === 404 || error?.response?.status === 404) {
        toast.error(
          error?.data?.error || error?.response?.data?.error || t("Integración no encontrada"),
        );
      } else {
        toast.error(
          t("Ha ocurrido un error al eliminar la integración. Por favor, intenta nuevamente."),
        );
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={t("Eliminar integración")}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={deleting}>
            {t("Cancelar")}
          </Button>
          <Button variant="danger" onClick={handleDelete} loading={deleting} disabled={deleting}>
            {deleting ? t("Eliminando...") : t("Eliminar integración")}
          </Button>
        </>
      }
    >
      <div className="flex items-start gap-3">
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-status-error-soft text-status-error"
          aria-hidden="true"
        >
          <AlertTriangle className="h-5 w-5" />
        </span>
        <div className="space-y-1 text-sm text-fg">
          <p>
            {t("Esta acción eliminará la integración con el alias")}{" "}
            <strong className="font-semibold">{alias}</strong>{" "}
            {t("de {{name}}.", { name: integrationName })}
          </p>
          <p className="text-fg-muted">{t("Esta acción no es reversible.")}</p>
          {/* Borrar acá solo quita el vínculo en Beartrack: la app sigue
              instalada en la tienda y sigue enviando pedidos, que ahora
              rebotan. El comercio tiene que desinstalarla él mismo. Se nombran
              las dos vías porque el portal no sabe por cuál se conectó la
              tienda (la app oficial o la app propia de la vía puente): ese
              dato es de la app de Shopify. */}
          {integrationName === "Shopify" && (
            <p className="rounded-lg border border-status-warning/40 bg-status-warning-soft px-3 py-2 text-fg">
              {t(
                "Eliminarla en Beartrack no desinstala la app de tu tienda. Para dejar de enviar pedidos, desinstala también Beartrack App (o la app que creaste para conectar Beartrack) desde Configuración → Apps en tu admin de Shopify.",
              )}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
